<template lang="pug">
el-table(v-loading="loading", :data="documents")
  el-table-column(width="50")
    template(#default="scope")
      a(:href="`/api/documents/${scope.row.uuid}`")
        el-icon
          Download
  el-table-column(:label="$t('description')", prop="description")
  el-table-column(v-if="mq.mdPlus", :label="$t('file')", prop="name")
  el-table-column(v-if="mq.mdPlus", :label="$t('changedBy')", prop="createdBy")
    template(#default="scope")
      UsersField(:user-ids="[scope.row.createdBy]")
  el-table-column(
    v-if="mq.mdPlus",
    :label="$t('changedAt')",
    prop="modified",
    :formatter="dateFormatter"
  )
  el-table-column(v-if="!readOnly", width="50")
    template(#default="scope")
      el-popover(width="300", trigger="click")
        template(#reference)
          el-icon.rotate-90.cursor-pointer.pt-4.pb-4
            More
        .flex.flex-col
          .mb-6.flex.items-center.cursor-pointer(
            v-for="c in categoryLinks",
            :key="c.category",
            @click="moveToCategory(scope.row, c.category)"
          )
            el-icon.mr-4
              Switch
            span {{ $t("moveToName", { name: $t(c.title) }) }}
          .mb-6.flex.items-center.cursor-pointer(
            @click="editDocument(scope.row)"
          )
            el-icon.mr-4
              Edit
            span {{ $t("edit") }}
          el-popconfirm(
            :title="$t('confirmDelete')",
            :confirm-button-text="$t('delete')",
            :cancel-button-text="$t('cancel')",
            width="220",
            @confirm="deleteDocument(scope.row.uuid)"
          )
            template(#reference)
              .flex.items-center.cursor-pointer
                el-icon.mr-4
                  Delete
                span {{ $t("delete") }}

.flex.justify-center.mt-5
  el-pagination(
    v-model:current-page="paging.page",
    layout="prev, pager, next",
    :page-count="totalPages",
    background,
    hide-on-single-page
  )

.flex.flex-row.justify-end.mb-4(v-if="!readOnly")
  el-button(
    v-if="mq.mdPlus",
    type="primary",
    icon="Upload",
    @click="openUploadDialog"
  ) {{ $t("uploadDocument") }}
  el-button(
    v-else,
    type="primary",
    icon="Upload",
    circle,
    @click="openUploadDialog"
  )

el-dialog(
  v-if="!readOnly",
  v-model="showEditDocumentDialog",
  :title="$t('editDocument')",
  :width="mq.mdPlus ? 500 : '100%'"
)
  smx-form
    smx-form-item(:label="$t('description')")
      el-input(v-model="description")
    template(#actions)
      .flex.items-end.w-full
      el-button(type="danger", @click="closeEditDocumentDialog") {{ $t("cancel") }}
      el-button(type="primary", @click="saveDescription") {{ $t("save") }}
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useTwinDocumentsStore } from "../../stores/documents";
import { useMq } from "vue3-mq";
import { formatDateTime } from "@shared/util/formatting";
import type { FileMetaData } from "../../services/documents";
import UsersField from "../twinManagement/components/UsersField.vue";
import { stateInitial, type PaginatedLoadState } from "@shared/models/state";

const props = withDefaults(
  defineProps<{
    twinId: string;
    category?: string;
    readOnly?: boolean;
    // A list of categories to which documents can be moved
    categoryLinks?: { title: string; category?: string }[];
  }>(),
  {
    category: "twinDocument",
    categoryLinks: () => [],
    readOnly: false,
  },
);

const mq = useMq();

const emit = defineEmits<{
  openUploadDialog: [category: string];
}>();

const documentsStore = useTwinDocumentsStore();

const loading = computed(() => loadState.value.loading);
const documents = computed(() => loadState.value.result ?? []);

const loadState = computed<PaginatedLoadState<FileMetaData[]>>(() => {
  const result = documentsStore.getTwinDocumentsByCategory(
    props.twinId,
    props.category,
  );
  return result || { ...stateInitial, result: [] };
});

const paging = ref({ page: 1, pageSize: 5 });

const totalPages = computed(() => loadState.value.pagesTotal ?? 0);

onMounted(() => {
  loadData();
});

const query = computed(() => ({
  type: "document",
  entityId: props.twinId,
  entityType: "twin",
  category: props.category,
}));

function loadData() {
  void documentsStore.loadTwinDocumentByCategory(
    props.category ?? "twinDocument",
    paging.value,
    query.value,
  );
}

watch(() => paging.value.page, loadData);

function dateFormatter(row: FileMetaData) {
  return formatDateTime(row.modified);
}

function deleteDocument(id: string) {
  void documentsStore.deleteTwinDocument(props.twinId, id, props.category);
}

const showEditDocumentDialog = ref(false);

const description = ref("");

const editingDocument = ref<FileMetaData | null>(null);

function openUploadDialog() {
  emit("openUploadDialog", props.category ?? "twinDocument");
}

function editDocument(document: FileMetaData) {
  showEditDocumentDialog.value = true;
  editingDocument.value = document;
  description.value = document.description;
}

function closeEditDocumentDialog() {
  description.value = "";
  editingDocument.value = null;
  showEditDocumentDialog.value = false;
}

function saveDescription() {
  if (editingDocument.value === null) {
    return;
  }
  void documentsStore.patchTwinDocument(
    props.twinId,
    editingDocument.value.uuid,
    {
      description: description.value,
    },
    props.category,
  );
  closeEditDocumentDialog();
}

function moveToCategory(file: FileMetaData, c?: string) {
  void documentsStore.changeTwinDocumentCategory(props.twinId, file, c);
}
</script>
